import { useEffect, useState } from "react";
import {
  formDisplayName,
  riskPriorityLabel,
  splitRiskFlagRows,
  type AiAssistOverviewFlag,
  type AttentionSource,
  type ClinicianRiskFlag,
  type PatientGroup,
  type RiskFlagRow,
} from "@care-by-ella/contracts";
import {
  acuteAlarmHeading,
  attentionAnswerAnchor,
  submittedAtSv,
} from "@/lib/attention-texts";
import { flagRuleLabel } from "@/lib/flag-rule-labels";
import { STATUS_TONE_CLASS } from "@/lib/status-tone";

// TES-948 (TES-941 §RISKFLAGGOR) — the ONE list of risk flags on the patient
// overview. Emelie's heading, exactly: "Riskflaggor: X". No qualifier such as
// "olösta" or "att följa upp": neither resolving nor following up is defined.
//
// Every flag and hard stop from every submission (the form-facts route,
// TES-943), with the open acute alarms (TES-676) split off by the same
// splitRiskFlagRows the API counts the header with, so each heading's number
// is its list's length.
//
// TES-1001 (Ö-250) — acute alarms are their own block ON TOP, "Akutlarm: X – agera nu",
// and are never also counted in "Riskflaggor: X". A-02:
//   "När en signal klassificeras som akut räknas den endast i akutlarmens antal"
// An alarm raised from a form's hard stop is ONE row in the acute block, with
// the answer behind it. An alarm keeps its own "Mottaget" and "Avsluta
// larmet"; nothing here changes the alarm lifecycle. Once closed, its hard
// stop falls back to the risk flag list, still visible.
//
// TES-1001 part 1 — a flag from the versioned risk-code list also shows its
// code, the list version and the priority the list gives it, on the same line.
//
// A click opens the row in place: the patient's own answer (free text
// verbatim), the question, the form's readable name and the date. Emelie:
// "Han ska aldrig behöva leta igenom hela formuläret för att förstå vad som flaggats."

function dateSv(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE");
}

function rowKey(row: RiskFlagRow, i: number): string {
  return row.alarm
    ? `alarm-${row.alarm.id}`
    : `${row.flag?.submissionId ?? row.flag?.templateKey}-${row.flag?.rule}-${i}`;
}

function Badge({ row }: { row: RiskFlagRow }) {
  const [text, cls] = row.alarm
    ? ["Larm", "bg-danger text-white"]
    : row.flag?.kind === "hard_stop"
      ? ["Stopp", "bg-notify text-white"]
      : // TES-1037 — a flag is the risk tone. It was peach, which is now
        // too close to the apricot that means "underlag saknas".
        ["Flagga", STATUS_TONE_CLASS.risk];
  return (
    <span
      className={`shrink-0 rounded-pill px-sm py-[3px] text-[11px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {text}
    </span>
  );
}

function FlagDetail({
  flag,
  formName,
  source,
}: {
  flag: AiAssistOverviewFlag;
  formName: (templateKey: string) => string;
  // TES-1034 — the whole submission the flag came from, when the form-facts
  // read carries it; the target of "Öppna källsvaret".
  source?: AttentionSource;
}) {
  const [showSource, setShowSource] = useState(false);
  const answers = flag.answers ?? [];
  const flagged = new Set(flag.fieldKeys ?? []);
  const firstAnchor =
    source && flag.fieldKeys?.[0]
      ? `flagga-${attentionAnswerAnchor(source.submissionId, flag.fieldKeys[0])}`
      : null;
  useEffect(() => {
    if (!showSource || !firstAnchor) return;
    document.getElementById(firstAnchor)?.scrollIntoView({ block: "center" });
  }, [showSource, firstAnchor]);
  return (
    <div className="mt-xs flex flex-col gap-sm rounded-md bg-surfaceMuted/40 p-sm text-sm">
      <p className="text-xs text-textSecondary">
        {formName(flag.templateKey)} · inskickat{" "}
        {submittedAtSv(flag.submittedAt)}
        {source ? (
          <>
            {" · "}
            <button
              type="button"
              onClick={() => setShowSource((v) => !v)}
              className="text-brand hover:underline"
            >
              {showSource ? "Dölj källsvaret" : "Öppna källsvaret"}
            </button>
          </>
        ) : null}
      </p>
      {answers.length > 0 ? (
        <dl className="flex flex-col gap-xs">
          {answers.map((a) => (
            <div key={a.fieldKey}>
              <dt className="text-xs text-textSecondary">
                {a.label ??
                  `Frågan finns inte i formulärversionen (${a.fieldKey})`}
              </dt>
              <dd className="whitespace-pre-wrap font-medium text-textPrimary">
                {a.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-xs text-textSecondary">
          Inget enskilt svar är kopplat till den här flaggan. Se formuläret för
          sammanhanget.
        </p>
      )}
      {flag.detail ? (
        <p className="text-xs text-textSecondary">{flag.detail}</p>
      ) : null}
      {showSource && source ? (
        <dl className="flex flex-col gap-xs rounded-md bg-surface p-sm">
          {source.answers.map((a) => {
            const anchor = `flagga-${attentionAnswerAnchor(source.submissionId, a.fieldKey)}`;
            return (
              <div
                key={anchor}
                id={anchor}
                className={
                  flagged.has(a.fieldKey) ? "rounded-sm bg-peach/40 p-xs" : ""
                }
              >
                <dt className="text-xs text-textSecondary">
                  {a.label ?? a.fieldKey}
                </dt>
                <dd className="whitespace-pre-wrap text-textPrimary">
                  {a.value}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : null}
    </div>
  );
}

export function RiskFlagsPanel({
  flags,
  flagsError,
  alarms,
  patientGroup,
  busyAlarmId,
  onAcknowledge,
  onClear,
  id,
  sources,
}: {
  // null while the form-facts request is in flight or failed; the alarms
  // still show, because they carry the buttons.
  flags: AiAssistOverviewFlag[] | null;
  flagsError?: string | null;
  alarms: ClinicianRiskFlag[];
  patientGroup?: PatientGroup | null;
  busyAlarmId?: string | null;
  onAcknowledge?: (alarmId: string) => void;
  onClear?: (alarmId: string) => void;
  // The anchor the "Medicinska larm" lane links to (#larm).
  id?: string;
  // TES-1034 — the flagged submissions' answers (form-facts
  // attention.sources), so an opened flag can link to its source answer.
  sources?: readonly AttentionSource[];
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const { acute, flags: flagRows } = splitRiskFlagRows(flags ?? [], alarms);
  const formName = (key: string) => formDisplayName(key, patientGroup);
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderRow = (row: RiskFlagRow, i: number) => {
    const key = rowKey(row, i);
    const expanded = open.has(key);
    const alarm = row.alarm;
    const rule = row.flag?.rule ?? alarm?.rule ?? alarm?.kind ?? "";
    return (
      <li key={key} className="text-sm">
        <div className="flex flex-wrap items-start gap-sm">
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => toggle(key)}
            className="flex min-w-0 flex-1 items-start gap-sm text-left hover:underline"
          >
            <Badge row={row} />
            <span className="min-w-0">
              <span title={rule}>{flagRuleLabel(rule)}</span>
              {row.flag?.riskCode ? (
                <span className="text-textSecondary">
                  {" "}
                  · Riskkod {row.flag.riskCode}
                  {row.flag.riskListVersion
                    ? ` (${row.flag.riskListVersion})`
                    : ""}{" "}
                  · Prioritet {riskPriorityLabel(row.flag.riskPriority)}
                </span>
              ) : null}
              <span className="text-textSecondary">
                {" "}
                ·{" "}
                {row.flag
                  ? `${formName(row.flag.templateKey)} ${dateSv(row.flag.submittedAt)}`
                  : `larm ${dateSv(alarm!.raisedAt)}`}
              </span>
            </span>
          </button>
          {alarm ? (
            <div className="ml-auto flex flex-wrap items-center gap-sm">
              {alarm.acknowledgedAt ? (
                <span className="text-xs text-textSecondary">
                  mottaget {dateSv(alarm.acknowledgedAt)}
                </span>
              ) : (
                <span className="rounded-pill bg-danger px-sm py-xs text-xs text-white">
                  Ej mottaget
                </span>
              )}
              {alarm.escalatedAt ? (
                <span className="text-xs text-danger">
                  eskalerat {dateSv(alarm.escalatedAt)}
                </span>
              ) : null}
              {!alarm.acknowledgedAt && onAcknowledge ? (
                <button
                  type="button"
                  disabled={busyAlarmId === alarm.id}
                  onClick={() => onAcknowledge(alarm.id)}
                  className="rounded-md bg-danger px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
                >
                  {busyAlarmId === alarm.id ? "Sparar…" : "Mottaget"}
                </button>
              ) : null}
              {onClear ? (
                <button
                  type="button"
                  disabled={busyAlarmId === alarm.id}
                  onClick={() => onClear(alarm.id)}
                  className="rounded-md border border-danger px-md py-xs text-sm text-danger hover:bg-danger/10 disabled:opacity-50"
                >
                  {busyAlarmId === alarm.id ? "Sparar…" : "Avsluta larmet"}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        {expanded ? (
          row.flag ? (
            <FlagDetail
              flag={row.flag}
              formName={formName}
              source={sources?.find(
                (src) => src.submissionId === row.flag?.submissionId,
              )}
            />
          ) : (
            <p className="mt-xs rounded-md bg-surfaceMuted/40 p-sm text-xs text-textSecondary">
              Larmet kommer inte från ett formulärsvar. Utlöst{" "}
              {dateSv(alarm!.raisedAt)}.
            </p>
          )
        ) : null}
      </li>
    );
  };

  return (
    <div id={id} className="flex flex-col gap-md">
      {acute.length > 0 ? (
        <section
          aria-label="Akutlarm"
          className="rounded-md border border-danger/40 bg-danger/5 p-md"
        >
          <h2 className="mb-sm font-display text-lg text-danger">
            {acuteAlarmHeading(acute.length)}
          </h2>
          <ul className="flex flex-col gap-sm">{acute.map(renderRow)}</ul>
        </section>
      ) : null}
      <section className="rounded-md border border-surfaceMuted bg-surface p-md">
        <h2 className="mb-sm font-display text-lg">
          Riskflaggor: {flagRows.length}
        </h2>
        {flagsError ? (
          <p className="mb-sm text-sm text-danger">{flagsError}</p>
        ) : flags === null ? (
          <p className="mb-sm text-sm text-textSecondary">
            Hämtar formulärens flaggor…
          </p>
        ) : null}
        {flagRows.length === 0 && flags !== null ? (
          <p className="text-sm text-textSecondary">
            Inga riskflaggor i formulären.
          </p>
        ) : (
          <ul className="flex flex-col gap-sm">{flagRows.map(renderRow)}</ul>
        )}
      </section>
    </div>
  );
}
